/**
 * 江南琴案 —— 设计 token（单一事实来源）
 *
 * 取值与 `_v2-designs/jiangnan-qinan.html` 一一对应。改配色只改这里。
 *
 * 三个出口（都从这里取值，不要就地写死颜色）：
 *   1. React 组件       → useTheme().palette（JS 对象）
 *   2. 裸 CSS / 全局态  → <GlobalStyle> 注入的 :root{--xxx}（由 palette 推导，不手写）
 *   3. 三个实验页 HTML  → 各自内联 :root{}（值需手工同步，见 DEVLOG）
 */

export type ThemeMode = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

export interface Palette {
	/** 粉墙白 · 页面底 */
	paper: string
	/** 宣纸 · 面板 */
	paper2: string
	/** 凹槽 · 输入框内底 */
	paper3: string
	/** 墨 · 正文 */
	ink: string
	/** 淡墨 · 次要文字 */
	ink2: string
	/** 更淡 · 元信息 */
	ink3: string
	/** 墨线 · 细边框 */
	line: string
	/** 稍重墨线 · 输入框边 */
	line2: string
	/** 青瓷 · 填充与装饰 */
	celadon: string
	/** 青瓷淡 · 选中底 */
	celadon2: string
	/** 青瓷墨 · 链接与激活（工作色） */
	celadonInk: string
	/** 朱砂 · 印章与主按钮，全站限量 */
	cinnabar: string
	/** 朱砂 hover */
	cinnabarD: string
	/** 藤黄 · 仅提示 */
	gold: string
}

export const light: Palette = {
	paper: '#f1f0ea',
	paper2: '#faf9f5',
	paper3: '#e7e6df',
	ink: '#1f2c2e',
	ink2: '#4e5b59',
	ink3: '#77827e',
	line: '#d9d7cc',
	line2: '#c4c1b4',
	celadon: '#6f9488',
	celadon2: '#dfe8e4',
	celadonInk: '#3f6b60',
	cinnabar: '#a8443a',
	cinnabarD: '#8c372e',
	gold: '#9a7b34',
}

export const dark: Palette = {
	paper: '#151c1d',
	paper2: '#1d2526',
	paper3: '#121819',
	ink: '#e5e5dc',
	ink2: '#a9b3af',
	ink3: '#7e8884',
	line: '#2d3739',
	line2: '#3b4648',
	celadon: '#6f9488',
	celadon2: '#23302f',
	celadonInk: '#9dc7b8',
	cinnabar: '#c4655a',
	cinnabarD: '#d3776c',
	gold: '#c2a05a',
}

/**
 * 阴影独立于 palette：同样是"三档"，但深浅主题的墨色不同（浅色用青灰、深色用纯黑）。
 */
export interface Shadows {
	/** 贴地 · 卡片 */
	sm: string
	/** 抬起 · 浮层 */
	lift: string
	/** 高浮 · 菜单/弹窗 */
	lg: string
}

export const shadowLight: Shadows = {
	sm: '0 1px 2px rgba(31,44,46,.06)',
	lift: '0 1px 2px rgba(31,44,46,.06), 0 6px 18px -8px rgba(31,44,46,.14)',
	lg: '0 2px 6px rgba(31,44,46,.08), 0 18px 44px -14px rgba(31,44,46,.22)',
}

export const shadowDark: Shadows = {
	sm: '0 1px 2px rgba(0,0,0,.4)',
	lift: '0 1px 2px rgba(0,0,0,.4), 0 6px 18px -8px rgba(0,0,0,.6)',
	lg: '0 2px 6px rgba(0,0,0,.5), 0 18px 44px -14px rgba(0,0,0,.7)',
}

/**
 * 字体栈 —— 分档 + 逐字回退。
 *
 * 自托管子集只覆盖固定 UI 文案；动态内容（曲名、歌词）逐字回退到系统宋体/黑体。
 * 这是本方案成立的关键：同为宋体类，混排可接受。
 *
 * ⚠️ 子集由 scripts/subset-fonts.mjs 生成；改这里的字族名要同步改脚本里的 FONTS。
 */
export const fonts = {
	serif: "'SBT Serif', 'Songti SC', 'SimSun', serif",
	sans: "'SBT Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif",
	brush: "'SBT Brush', 'SBT Serif', serif",
	mono: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Consolas, monospace",
}

/** 字号六档 */
export const fontSize = {
	xs: 11,
	sm: 13,
	base: 15,
	md: 18,
	lg: 22,
	xl: 30,
}

/** 圆角三档 —— 小圆角是本设计的骨架特征，别再散写 8px/12px */
export const radius = {
	sm: 3,
	md: 4,
	lg: 6,
}

/** 全站统一断点（含 saas 两页，替代历史上的 640px） */
export const BREAKPOINT = 900

/** 主题持久化键 */
export const THEME_STORAGE_KEY = 'sbt-theme'

/** palette → CSS 自定义属性。GlobalStyle 与实验页都从这里推导，避免两处手抄。 */
export function paletteToCssVars(p: Palette): Record<string, string> {
	return {
		'--paper': p.paper,
		'--paper-2': p.paper2,
		'--paper-3': p.paper3,
		'--ink': p.ink,
		'--ink-2': p.ink2,
		'--ink-3': p.ink3,
		'--line': p.line,
		'--line-2': p.line2,
		'--celadon': p.celadon,
		'--celadon-2': p.celadon2,
		'--celadon-ink': p.celadonInk,
		'--cinnabar': p.cinnabar,
		'--cinnabar-d': p.cinnabarD,
		'--gold': p.gold,
	}
}

export function shadowsToCssVars(s: Shadows): Record<string, string> {
	return {
		'--shadow-sm': s.sm,
		'--shadow-lift': s.lift,
		'--shadow-lg': s.lg,
	}
}

/**
 * 字体栈也要出 CSS 变量：有些地方是**内嵌 HTML 字符串**或 JSS 管不到的原生元素
 * （OSMD 加载提示、主题按钮、导出的独立 HTML），那里写不了 JS 对象，只能靠
 * `var(--font-sans)` 取字体。主题色同理。
 */
export function fontsToCssVars(): Record<string, string> {
	return {
		'--font-serif': fonts.serif,
		'--font-sans': fonts.sans,
		'--font-brush': fonts.brush,
		'--font-mono': fonts.mono,
	}
}

/** 把 token 摊平成 CSS 声明串（不含选择器） */
export function tokenCssDecls(p: Palette, s: Shadows): string {
	const vars = { ...paletteToCssVars(p), ...shadowsToCssVars(s), ...fontsToCssVars() }
	return Object.entries(vars)
		.map(([k, v]) => `\t${k}: ${v};`)
		.join('\n')
}
